/**
 * @typedef {Object} MaestraRecommendationIds
 * @property {number} productId
 * @property {string} shopifyid
 */

/**
 * @typedef {Object} MaestraRecommendationItem
 * @property {MaestraRecommendationIds} ids
 * @property {string} name
 * @property {string} url
 * @property {number} price
 */

/**
 * @typedef {Object} MaestraRecommendationResponse
 * @property {string} status
 * @property {MaestraRecommendationItem[]} recommendations
 */

const Maestra = {
    init: function () {
        this.initFbtRecommendations();
    },

    initFbtRecommendations: function () {
        const container = document.querySelector('.maestra-fbt-container');

        if (!container) {
            return;
        }

        if (container.dataset.useMaestra === 'false') {
            return;
        }

        // Trigger fetch immediately on load to ensure content is ready before the user scrolls.
        this.fetchFbtRecommendations(container);
    },

    triggerFbtLoadedEvent: function (container) {
        const resultsContainer = container.querySelector('#maestra-fbt-results');

        if (resultsContainer) {
            // Notify global scripts to bind swatch and cart event listeners to the new nodes.
            const customEvent = new CustomEvent('maestra:fbt:loaded', {
                detail: {
                    container: resultsContainer
                }
            });
            document.dispatchEvent(customEvent);
        }
    },

    /**
     * Fetch PDP recommendations from Maestra using the OftenBoughtTogether endpoint,
     * which requires a Shopify Variant ID.
     *
     * @param {string|number} variantShopifyId Shopify Variant ID (required)
     * @param {number} [limit=8] Maximum number of recommendations to return
     * @param {string} [baseUrl="https://api.selkirk.com/functions/v1/p1/selkirksport/maestra"] Base API URL
     * @returns {Promise<{ ok: boolean, status: number, statusMessage: string, data: any, raw: any }>}
     */
    getMaestraPdpRecommendations: async function ({
        variantShopifyId,
        limit = 8,
        baseUrl = "https://api.selkirk.com/functions/v1/p1/selkirksport/maestra"
    }) {
        if (!variantShopifyId) {
            return {
                ok: false,
                error: "variantShopifyId is required",
                status: 400,
                statusMessage: "Bad Request",
                data: null,
                raw: null
            };
        }

        const variantIdString = String(variantShopifyId);

        const url = `${baseUrl}/oftenBoughtTogether`;
        const body = {
            recommendation: {
                limit: limit,
                product: {
                    ids: {
                        shopifyid: variantIdString
                    }
                }
            }
        };

        try {
            const resp = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "x-region": "us-west-1"
                },
                body: JSON.stringify(body)
            });

            let json = null;
            try {
                json = await resp.json();
            } catch (e) {
                console.warn("Failed to parse JSON:", e);
            }

            return {
                ok: resp.ok,
                status: json?.status ?? resp.status,
                statusMessage: json?.statusMessage ?? resp.statusText,
                data: json?.data ?? json ?? null,
                raw: json
            };
        } catch (error) {
            console.error("Error fetching Maestra PDP Recommendations:", error);
            return {
                ok: false,
                status: 0,
                statusMessage: "Network Error",
                error: "Failed to fetch recommendations",
                data: null,
                raw: null
            };
        }
    },

    fetchFbtRecommendations: async function (container) {
        // Extract required data and target DOM node.
        const variantId = container.dataset.variantId;
        const resultsContainer = container.querySelector('#maestra-fbt-results');

        // Trigger loading state for CSS transitions.
        container.classList.add('is-loading');

        try {
            // Fetch recommendations from Maestra API.
            const response = await this.getMaestraPdpRecommendations({
                variantShopifyId: variantId,
                limit: 4
            });

            if (response.ok && response.data && Array.isArray(response.data.recommendations) && response.data.recommendations.length > 0) {
                // Map recommendations to Shopify alternate template fetch requests.
                const cardPromises = response.data.recommendations.map((item) => {
                    try {
                        // Parse URL defensively to handle relative or malformed paths.
                        const urlObject = new URL(item.url, window.location.origin);
                        const handle = urlObject.pathname.split('/').pop();
                        const recommendedVariantId = item.ids.shopifyid;

                        return this.fetchProductCard(handle, recommendedVariantId, 'fbt-card');
                    } catch (error) {
                        console.warn('Skipping Maestra recommendation with invalid URL:', item.url, error);
                        return null;
                    }
                });

                // Await all product card HTML responses.
                const cardsHtml = await Promise.all(cardPromises);

                // Filter out any failed requests.
                const validCards = cardsHtml.filter((html) => {
                    return html !== null;
                });

                if (validCards.length > 0) {
                    // Inject the successfully rendered HTML into the DOM.
                    resultsContainer.innerHTML = validCards.join('');
                    container.classList.add('has-maestra');

                    this.triggerFbtLoadedEvent(container);
                    container.classList.remove('is-loading');
                    return;
                }
            }
        } catch (error) {
            console.error('Error loading Maestra FBT:', error);
        }
        // Remove loading state regardless of success or failure.
        container.classList.remove('is-loading');
    },

    initCartBestsellers: function () {
        // Future sidebar cart implementation.
    },

    fetchProductCard: async function (handle, variantId, viewTemplate) {
        if (!handle) {
            return null;
        }

        try {
            const response = await fetch(`/products/${handle}?view=${viewTemplate}&variant=${variantId}`);

            if (!response.ok) {
                return null;
            }

            return await response.text();
        } catch (error) {
            console.error(`Error fetching product card for ${handle}:`, error);
            return null;
        }
    }
};

document.addEventListener('DOMContentLoaded', () => {
    Maestra.init();
});